/**
 * Item Ledger — Frontend ローカル Contract
 *
 * BE 側 `dresscode-backend/v2/src/business-central/api/item-ledger/usecase/item-ledger.api-contract.ts`
 * と同じ Zod 構造を再現。FE-2 時点では `@standandforce/api-contract` 0.0.960 に
 * `itemLedgerContract` が publish されていないため、ローカルに最低限の Contract を定義する。
 * 共有パッケージへ publish された後は client.ts でローカル import を置き換える想定。
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { paginationQuerySchema } from './pagination';

// ─── Enum values（BE と同期） ───

/** Item Status（品目の運用状態）。台帳エントリの生死を担う */
export const itemStatusValues = ['DRAFT', 'ACTIVE', 'BLOCKED', 'OBSOLETE', 'ARCHIVED'] as const;
export type ItemStatus = (typeof itemStatusValues)[number];

/** Item ステータス変更 API のターゲット値（DRAFT は ItemCreated でのみ到達するため除外）。 */
export const itemStatusChangeTargetValues = ['ACTIVE', 'BLOCKED', 'OBSOLETE', 'ARCHIVED'] as const;
export type ItemStatusChangeTarget = (typeof itemStatusChangeTargetValues)[number];

// ─── DataGrid Response Schema（customer-ledger と同形） ───

const DataGridColumnSchema = z.object({
  fieldKey: z.string(),
  fieldName: z.string(),
  cellType: z.string(),
  sortable: z.boolean(),
  pinned: z.string().nullable(),
  minWidth: z.string().nullable()
});
const DataGridSchemaSchema = z.object({
  columns: z.array(DataGridColumnSchema),
  defaultSort: z.object({
    fieldKey: z.string(),
    direction: z.enum(['asc', 'desc'])
  }).nullable()
});
const DataGridItemSchema = z.object({
  id: z.string(),
  fieldValues: z.record(z.string(), z.unknown())
});
const DataGridResponseSchema = z.object({
  schema: DataGridSchemaSchema,
  items: z.array(DataGridItemSchema),
  count: z.number(),
  page: z.number(),
  maxPage: z.number()
});

// ─── Input Schema ───

// orderBy は DataGrid の fieldKey を受け取り、BE 側で sortKey に解決する。
// orderDirection は DataGrid 既定ソートを優先するため optional で上書きする。
export const ItemsListInputSchema = paginationQuerySchema(z.string()).extend({
  orderDirection: z.enum(['asc', 'desc']).optional(),
  searchText: z.string().nullable().default(null),
  statuses: z.array(z.enum(itemStatusValues)).nullable().default(null),
  itemTypeIds: z.array(z.string()).nullable().default(null)
});
export type ItemsListInput = z.infer<typeof ItemsListInputSchema>;

// ─── Write Schemas (Create) ───

const ItemCreateInputSchema = z.object({
  name: z.string(),
  itemTypeId: z.string(),
  baseUnit: z.string(),
  description: z.string().nullish()
});
const ItemCreateOutputSchema = z.object({
  itemId: z.string(),
  itemNumber: z.string(),
  status: z.literal('DRAFT')
});
export type ItemCreateInput = z.infer<typeof ItemCreateInputSchema>;
export type ItemCreateOutput = z.infer<typeof ItemCreateOutputSchema>;

// ─── Write Schemas (Update / ChangeStatus) ───

const ItemUpdateInputSchema = z.object({
  itemId: z.string(),
  name: z.string().optional(),
  itemTypeId: z.string().optional(),
  description: z.string().nullish()
});
const ItemUpdateOutputSchema = z.object({
  itemId: z.string(),
  version: z.number()
});
const ItemChangeStatusInputSchema = z.object({
  itemId: z.string(),
  targetStatus: z.enum(itemStatusChangeTargetValues),
  reason: z.string().nullish()
});
const ItemChangeStatusOutputSchema = z.object({
  itemId: z.string(),
  status: z.enum(itemStatusValues),
  version: z.number()
});
export type ItemUpdateInput = z.infer<typeof ItemUpdateInputSchema>;
export type ItemUpdateOutput = z.infer<typeof ItemUpdateOutputSchema>;
export type ItemChangeStatusInput = z.infer<typeof ItemChangeStatusInputSchema>;
export type ItemChangeStatusOutput = z.infer<typeof ItemChangeStatusOutputSchema>;

// ─── Detail Schemas（BE PR7 `item-ledger.api-contract.ts` の写し） ───

const ItemIdInputSchema = z.object({
  itemId: z.string()
});
const ItemIdentitySchema = z.object({
  itemId: z.string(),
  itemNumber: z.string(),
  name: z.string(),
  itemTypeId: z.string(),
  itemTypeCode: z.string(),
  itemTypeName: z.string(),
  status: z.enum(itemStatusValues),
  baseUnit: z.string(),
  description: z.string().nullable(),
  // oRPC は JSON transport のため BE の Date は ISO 文字列として届く。
  // z.date() は文字列を受け付けないので、`z.coerce.date()` で ISO → Date に変換する。
  // FE 側の formatDateTimeToLocal は Date 型を要求するため、この時点で Date へ寄せる。
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});
const ItemProcurementProfileSchema = z.object({
  itemProcurementProfileId: z.string(),
  leadTimeDays: z.number().nullable(),
  minOrderQty: z.number().nullable(),
  standardPrice: z.number().nullable(),
  currency: z.string().nullable()
});
const ItemDetailOutputSchema = z.object({
  identity: ItemIdentitySchema,
  procurementProfile: ItemProcurementProfileSchema.nullable()
});
export type ItemDetailOutput = z.infer<typeof ItemDetailOutputSchema>;
export type ItemIdentity = z.infer<typeof ItemIdentitySchema>;
export type ItemProcurementProfile = z.infer<typeof ItemProcurementProfileSchema>;

// ─── Contract ───

export const itemLedgerContract = oc.router({
  itemsList: oc.route({
    method: 'GET',
    path: '/v2/business-central/item-ledger/items'
  }).input(ItemsListInputSchema).output(DataGridResponseSchema),
  itemsDetail: oc.route({
    method: 'GET',
    path: '/v2/business-central/item-ledger/items/{itemId}'
  }).input(ItemIdInputSchema).output(ItemDetailOutputSchema),
  itemsCreate: oc.route({
    method: 'POST',
    path: '/v2/business-central/item-ledger/items'
  }).input(ItemCreateInputSchema).output(ItemCreateOutputSchema),
  itemsUpdate: oc.route({
    method: 'PATCH',
    path: '/v2/business-central/item-ledger/items/{itemId}'
  }).input(ItemUpdateInputSchema).output(ItemUpdateOutputSchema),
  itemsChangeStatus: oc.route({
    method: 'POST',
    path: '/v2/business-central/item-ledger/items/{itemId}/status'
  }).input(ItemChangeStatusInputSchema).output(ItemChangeStatusOutputSchema)
});